import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Sticky from 'react-stickynode';
import _throttle from 'lodash/throttle';
import cx from 'classnames';

import { NAV_LOADED } from 'constants/navConstants';

import { PLP_MODULE_GROUP_TAGS } from 'cards/constants/CardTypes';
import SkinnyBanner from 'components/promotions/SkinnyBanner';

import SecondaryNavDataV2 from 'components/common/header/SecondaryNavV2/SecondaryNavDataV2';
import { emitUpdateNavDataEvent, getNavHeight } from 'components/common/header/helpers';

// util is not aliased, see webpack config for why
import InnerHTML from '../../../util/ScriptInnerHtml';
import styles from './styles.module.less';

const SECONDARY_NAV_WEBSITE = 'website';
const SECONDARY_NAV_INVITES = 'invites';
const SECONDARY_NAV_GUESTLIST = 'guestlist';

const UnifiedNav = ({
  className,
  onGetNav,
  fetchUserContext,
  showPromo,
  activePrimaryLink,
  activeSecondaryLink,
  disablePrimaryNavCollapse,
  disablePromoNavCollapse,
  enableSticky,
  activeCardEvent,
  albumEvent,
  isHidden,
  userContext,
  navItems,
  secondaryType,
  emitDirectionEvents,
  unstickBanner,
}) => {
  const [height, setHeight] = useState(getNavHeight());
  const [nav, setNav] = useState('');

  // useMemo so that the event listener hook uses correct ref equality
  const secondaryData = useMemo(() => {
    let data;
    if (navItems) {
      data = navItems;
    } else if (secondaryType === SECONDARY_NAV_INVITES) {
      data = new SecondaryNavDataV2(userContext).getInvitesItems();
    } else if (secondaryType === SECONDARY_NAV_GUESTLIST) {
      data = new SecondaryNavDataV2(userContext).getGuestListItems();
    } else if (secondaryType === SECONDARY_NAV_WEBSITE) {
      data = new SecondaryNavDataV2(userContext).getWebsiteItems();
    }
    return data;
  }, [navItems, userContext, secondaryType]);

  const refreshNavHeight = _throttle(() => {
    const newHeight = getNavHeight(secondaryData);
    if (height !== newHeight) {
      setHeight(newHeight);
    }
  }, 200);

  useEffect(() => {
    const handleInitNavData = () => {
      emitUpdateNavDataEvent({
        userContext,
        showPromo,
        secondaryData,
        activePrimaryLink,
        activeSecondaryLink,
        disablePrimaryNavCollapse,
        disablePromoNavCollapse,
        emitDirectionEvents,
      });
    };
    if (!nav) {
      onGetNav().then(res => {
        setNav(res);
        refreshNavHeight();
      });
    }

    if (!userContext) {
      fetchUserContext();
    }

    window.addEventListener(NAV_LOADED, handleInitNavData);
    window.addEventListener('resize', refreshNavHeight);

    return () => {
      window.removeEventListener(NAV_LOADED, handleInitNavData);
      window.removeEventListener('resize', refreshNavHeight);
    };
  }, [
    secondaryData,
    userContext,
    activeSecondaryLink,
    fetchUserContext,
    nav,
    onGetNav,
    refreshNavHeight,
    activePrimaryLink,
    disablePrimaryNavCollapse,
    disablePromoNavCollapse,
    showPromo,
    emitDirectionEvents,
  ]);

  const defaultStyles = { height, transform: 'none' };
  const hiddenStyles = isHidden ? { position: 'absolute', left: '-99999px' } : {};

  return (
    <>
      <div className={cx('unified-nav', className)} style={{ ...defaultStyles, ...hiddenStyles }}>
        <Sticky innerZ={500} enableTransforms={false} enabled={enableSticky}>
          <InnerHTML html={nav} />
          {activeCardEvent && !unstickBanner && (
            <SkinnyBanner tag={PLP_MODULE_GROUP_TAGS[activeCardEvent]} />
          )}
          {albumEvent && !unstickBanner && <SkinnyBanner tag="album-pdp" />}
        </Sticky>
      </div>
      {activeCardEvent && unstickBanner && (
        <div className={styles.bannerWrapper}>
          <SkinnyBanner tag={PLP_MODULE_GROUP_TAGS[activeCardEvent]} />
        </div>
      )}
    </>
  );
};

UnifiedNav.propTypes = {
  className: PropTypes.string,
  fetchUserContext: PropTypes.func.isRequired,
  onGetNav: PropTypes.func,
  enableSticky: PropTypes.bool,
  showPromo: PropTypes.bool,
  activePrimaryLink: PropTypes.string,
  activeSecondaryLink: PropTypes.string,
  activeCardEvent: PropTypes.string,
  albumEvent: PropTypes.string,
  isHidden: PropTypes.bool,
  disablePrimaryNavCollapse: PropTypes.bool,
  disablePromoNavCollapse: PropTypes.bool,
  userContext: PropTypes.shape({}),
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      path: PropTypes.string,
    })
  ),
  secondaryType: PropTypes.oneOf([
    SECONDARY_NAV_INVITES,
    SECONDARY_NAV_WEBSITE,
    SECONDARY_NAV_GUESTLIST,
  ]),
  emitDirectionEvents: PropTypes.bool,
};

UnifiedNav.defaultProps = {
  enableSticky: true,
};

export default UnifiedNav;



// WEBPACK FOOTER //
// ./src/components/common/header/UnifiedNav/UnifiedNav.jsx